// Page-picker screen — lands here after the OAuth callback when the
// user granted access to 2+ FB Pages. Backend wrote a pending row +
// redirected the browser here. We fetch the pages list, render each as
// a clickable card with an IG badge (if the Page has Instagram linked),
// and POST the chosen page_id to /finalize.
//
// Single-page grants don't hit this screen — the callback auto-finalizes
// and bounces straight to /gallery. So if this page loads with an
// empty pending list it means either:
//   (a) The pending row expired (>10 min since OAuth — user took too long)
//   (b) User opened /connect-facebook directly without an OAuth grant
//   (c) The OAuth flow already auto-finalized (single-page user)
// All three cases: redirect to /gallery silently.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import {
  getFacebookPendingPages,
  finalizeFacebookConnection,
  type FacebookPendingPage,
} from '@/lib/api';

type ViewState =
  | { kind: 'loading' }
  | { kind: 'empty' }                            // no pending, will redirect
  | { kind: 'ready'; pages: FacebookPendingPage[] }
  | { kind: 'submitting'; pageId: string }
  | { kind: 'error'; message: string; pages: FacebookPendingPage[] };

export default function ConnectFacebookPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<ViewState>({ kind: 'loading' });

  // Fetch pending list on mount. Empty result → redirect to /gallery
  // (covers expired pending + direct-URL-visit cases — see header comment).
  useEffect(() => {
    void (async () => {
      try {
        const pages = await getFacebookPendingPages();
        if (pages.length === 0) {
          setView({ kind: 'empty' });
          // Brief delay so the redirect feels intentional vs a flash.
          setTimeout(() => navigate('/gallery', { replace: true }), 600);
          return;
        }
        setView({ kind: 'ready', pages });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Could not load Facebook pages';
        setView({ kind: 'error', message: msg, pages: [] });
      }
    })();
  }, [navigate]);

  const handlePick = async (page: FacebookPendingPage) => {
    if (view.kind === 'submitting') return;
    const prevPages = 'pages' in view ? view.pages : [];
    setView({ kind: 'submitting', pageId: page.id });
    try {
      const status = await finalizeFacebookConnection(page.id);
      // Build the return-toast query string the same way the OAuth
      // callback does — gallery's URL-param handler shows the toast.
      const params = new URLSearchParams();
      params.set('fb', 'connected');
      if (status.pageName) params.set('page', status.pageName);
      if (status.instagramUsername) params.set('ig', status.instagramUsername);
      // Hard-nav so AppLayoutV2's connection-status fetch fires fresh.
      window.location.assign(`/gallery?${params.toString()}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not connect this page';
      setView({ kind: 'error', message: msg, pages: prevPages });
    }
  };

  return (
    <AppLayoutV2>
      <div className="max-w-2xl mx-auto px-6 py-10">
        {/* Header — Facebook-blue brand chip + title. */}
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
            style={{ backgroundColor: '#1877F2' }}
          >
            <i className="ri-facebook-circle-line text-2xl" />
          </div>
          <div>
            <h1 className="font-display font-bold text-dark text-2xl leading-tight">
              Choose a Facebook Page
            </h1>
            <p className="text-dark-muted text-sm">
              You granted access to multiple Pages. Pick the one this app should
              post to.
            </p>
          </div>
        </div>

        {/* Status pane. */}
        {view.kind === 'loading' && (
          <div className="mt-8 flex items-center gap-3 text-dark-muted">
            <i className="ri-loader-4-line animate-spin text-primary text-xl" />
            <span>Loading your Facebook Pages…</span>
          </div>
        )}

        {view.kind === 'empty' && (
          <div className="mt-8 p-4 rounded-xl bg-surface border border-surface-dark/40 text-dark-muted text-sm">
            Nothing to connect — sending you back to My Creatives.
          </div>
        )}

        {view.kind === 'error' && view.pages.length === 0 && (
          <div className="mt-8 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            <i className="ri-error-warning-line mr-1.5" />
            {view.message}
            <div className="mt-3">
              <button
                onClick={() => navigate('/gallery', { replace: true })}
                className="text-primary text-sm font-bold hover:underline"
              >
                Back to My Creatives →
              </button>
            </div>
          </div>
        )}

        {/* Page list — visible when ready / submitting / error-with-pages. */}
        {'pages' in view && view.pages.length > 0 && (
          <div className="mt-6 space-y-2">
            {view.pages.map((p) => {
              const isSubmitting = view.kind === 'submitting' && view.pageId === p.id;
              const disabled = view.kind === 'submitting';
              return (
                <button
                  key={p.id}
                  onClick={() => void handlePick(p)}
                  disabled={disabled}
                  className="
                    w-full text-left rounded-2xl border border-surface-dark/40
                    bg-white p-4 flex items-center gap-4
                    hover:border-primary hover:shadow-md hover:shadow-primary/10
                    transition-all cursor-pointer
                    disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:border-surface-dark/40
                  "
                >
                  {/* Page avatar slot — FB brand chip. */}
                  <div
                    className="w-12 h-12 flex-shrink-0 rounded-xl flex items-center justify-center text-white"
                    style={{ backgroundColor: '#1877F2' }}
                  >
                    <i className="ri-facebook-circle-line text-xl" />
                  </div>
                  {/* Page name + IG badge (or "no IG linked" hint). */}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-dark text-base truncate">
                      {p.name}
                    </div>
                    {p.category && (
                      <div className="text-xs text-dark-muted truncate">
                        {p.category}
                      </div>
                    )}
                    {p.instagramUsername ? (
                      <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold"
                           style={{ backgroundColor: '#E4405F14', color: '#E4405F' }}>
                        <i className="ri-instagram-line" />
                        Instagram: @{p.instagramUsername}
                      </div>
                    ) : (
                      <div className="mt-1.5 inline-block text-[10px] text-dark-muted/70 italic">
                        No Instagram linked to this Page
                      </div>
                    )}
                  </div>
                  {/* Right-side spinner or arrow. */}
                  {isSubmitting ? (
                    <i className="ri-loader-4-line animate-spin text-primary text-xl flex-shrink-0" />
                  ) : (
                    <i className="ri-arrow-right-line text-dark-muted text-xl flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Error after a failed finalize — leave the page list shown above
            so the user can retry with another choice. */}
        {view.kind === 'error' && view.pages.length > 0 && (
          <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">
            <i className="ri-error-warning-line mr-1.5" />
            {view.message}
          </div>
        )}

        {/* Cancel — bounce back to gallery without committing. The pending
            row stays in DB until the user re-OAuths (which UPSERTs it) or
            the 10-min TTL passes. */}
        <div className="mt-8">
          <button
            onClick={() => navigate('/gallery', { replace: true })}
            className="text-sm text-dark-muted hover:text-dark font-bold"
          >
            ← Skip for now
          </button>
        </div>
      </div>
    </AppLayoutV2>
  );
}
